import { IHero } from "@src/components/Hero";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import { getCookie } from "cookies-next";
import { motion } from "framer-motion";
import Image from "next/image";
import React from "react";
import Dialog from "@src/components/Dialog";
import { useFloater } from "@src/hooks/useFloater";
import {
  EventBySlugQuery,
  EventPartsQuery,
  GetAseobiQuery,
  GetEventStreamersQuery,
  GetLiveScheduleQuery,
} from "@src/@types/api.d";
import MonetHeader, { HeaderNavItem } from "../Header";
import Footer from "../Footer";
import flowerBackground from "../../../../public/images/bw-flower.jpeg";
import { parseEventDate } from "@src/utils/dateFormatter";

interface MonetHeroProps extends IHero {
  getEventStreamersData?: GetEventStreamersQuery;
  liveSchedules?: GetLiveScheduleQuery;
  accessToken?: string | null;
  eventUuid?: string;
  getAsoebiData?: GetAseobiQuery;
  admits?: number;
  invitation?: any;
  isInvitedToEvent?: boolean;
  qrCode?: string;
  navItems?: HeaderNavItem[];
  activeNavId?: string;
  onNavigate?: (id: string) => void;
  eventDate?: string | null;
  eventPartsData?: EventPartsQuery;
}

export type EventSlugEventParts = NonNullable<
  EventBySlugQuery["eventBySlug"]
>["event_parts"];

const getFirstValidDate = (dates: Array<string | null | undefined>) =>
  dates.find((date) => !!date && !!parseEventDate(date)) ?? null;

const getPublicEventPartDates = (eventParts?: EventSlugEventParts | null) =>
  (eventParts ?? []).map((part) => part?.time).filter(Boolean);

const getEventPartDates = (eventPartsData?: EventPartsQuery) =>
  (eventPartsData?.eventParts ?? []).map((part) => part.time);

const formatHeroDate = (date?: string | null) => {
  const parsed = parseEventDate(date);
  if (!parsed) return "";

  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const getCountdown = (date?: string | null) => {
  const target = parseEventDate(date)?.getTime() ?? 0;
  const diff = Math.max(target - Date.now(), 0);
  const day = 1000 * 60 * 60 * 24;
  const hour = 1000 * 60 * 60;
  const minute = 1000 * 60;

  return {
    days: Math.floor(diff / day),
    hours: Math.floor((diff % day) / hour),
    mins: Math.floor((diff % hour) / minute),
    secs: Math.floor((diff % minute) / 1000),
  };
};

const splitNames = (name?: string | null) => {
  if (!name) return ["", ""];

  const ampersandParts = name.split(/\s*&\s*/);
  if (ampersandParts.length >= 2) {
    return [ampersandParts[0], ampersandParts.slice(1).join(" & ")];
  }

  const andParts = name.split(/\s+and\s+/i);
  if (andParts.length >= 2) {
    return [andParts[0], andParts.slice(1).join(" and ")];
  }

  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return [words[0], words.slice(1).join(" ")];
  }

  return [name, ""];
};

const isImageUrl = (url?: string | null) => {
  if (!url) return false;

  const cleanUrl = url.split("?")[0].toLowerCase();
  return /\.(avif|gif|jpe?g|png|webp)$/.test(cleanUrl);
};

const Hero: React.FC<MonetHeroProps> = ({
  heroImageData,
  slugData,
  eventId,
  loading,
  openInvitationModal,
  albumDetailsData,
  getEventStreamersData,
  liveSchedules,
  accessToken,
  eventUuid,
  getAsoebiData,
  admits = 1,
  invitation,
  isInvitedToEvent = false,
  qrCode = "",
  navItems = [],
  activeNavId = "hero",
  onNavigate,
  eventDate: eventDateOverride,
  eventPartsData,
}) => {
  const [activeHeroImageIndex, setActiveHeroImageIndex] = React.useState(0);
  const currentAccessToken = accessToken ?? (getCookie("tsl") as string);
  const event = slugData?.eventBySlug;
  const eventName = event?.name ?? "";
  const [firstName, secondName] = splitNames(eventName);
  const resolvedEventDate = getFirstValidDate([
    eventDateOverride,
    event?.date,
    ...getPublicEventPartDates(event?.event_parts),
    ...getEventPartDates(eventPartsData),
  ]);
  const eventDate = formatHeroDate(resolvedEventDate);
  const [countdown, setCountdown] = React.useState(() =>
    getCountdown(resolvedEventDate)
  );
  const heroImages = React.useMemo(() => {
    const candidates = [
      heroImageData?.getHeroImage,
      event?.hero_image_thumbnail,
      ...(albumDetailsData?.getMedia ?? [])
        .map((media) => media?.meta?.s3_url || media?.url)
        .filter(isImageUrl),
    ].filter(Boolean) as string[];

    return Array.from(new Set(candidates));
  }, [
    albumDetailsData?.getMedia,
    event?.hero_image_thumbnail,
    heroImageData?.getHeroImage,
  ]);
  const heroImage = heroImages[activeHeroImageIndex] || flowerBackground;
  const location = event?.event_parts?.find(
    (part) => part?.city || part?.state || part?.country
  );
  const locationText = [location?.city, location?.state, location?.country]
    .filter(Boolean)
    .join(", ");
  const { filteredDeck, trackView } = useFloater({
    getEventStreamersData: getEventStreamersData ?? {
      getEventStreamers: null,
    },
    eventId,
    slugData,
    liveSchedules: liveSchedules ?? {
      getLiveSchedule: {
        page: 1,
        pageSize: 0,
        pageCount: 0,
        recordCount: 0,
        data: [],
      },
    },
    access_control: event?.event_preference?.access_control ?? false,
    qr_code: qrCode,
    accessToken: currentAccessToken ?? null,
    getAsoebiData,
    eventUuid,
    admits,
    invitation,
    isInvitedToEvent,
  });
  const rsvpItem = filteredDeck.find((item) => item.title === "RSVP");
  const asoebiItem = filteredDeck.find((item) => item.title === "Aso ebi");

  React.useEffect(() => {
    setCountdown(getCountdown(resolvedEventDate));

    const countdownTimer = window.setInterval(() => {
      setCountdown(getCountdown(resolvedEventDate));
    }, 1000);

    return () => window.clearInterval(countdownTimer);
  }, [resolvedEventDate]);

  React.useEffect(() => {
    setActiveHeroImageIndex(0);

    if (heroImages.length <= 1) return;

    const imageTimer = window.setInterval(() => {
      setActiveHeroImageIndex((current) => (current + 1) % heroImages.length);
    }, 3000);

    return () => window.clearInterval(imageTimer);
  }, [heroImages.length]);

  const countdownItems = [
    ["days", countdown.days],
    ["hrs", countdown.hours],
    ["mins", countdown.mins],
    ["secs", countdown.secs],
  ];

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-[#1C1820] text-white">
      <MonetHeader
        slugData={slugData}
        heroImageData={heroImageData}
        navItems={navItems}
        activeId={activeNavId}
        onNavigate={onNavigate || openInvitationModal || (() => undefined)}
      />

      <section className="relative flex min-h-screen w-full items-center justify-center overflow-hidden px-5 pt-[68px] text-center">
        {heroImages.length > 0 ? (
          heroImages.map((image, index) => (
            <motion.div
              key={image}
              initial={false}
              animate={{
                opacity: index === activeHeroImageIndex ? 1 : 0,
                scale: index === activeHeroImageIndex ? 1 : 1.06,
              }}
              transition={{
                opacity: { duration: 0.9 },
                scale: { duration: 3.4 },
              }}
              className="absolute inset-0"
            >
              <Image
                placeholder="blur"
                loading={index === 0 ? "eager" : "lazy"}
                layout="fill"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(700, 900)
                )}`}
                src={image}
                alt={eventName || "Wedding couple"}
                className="object-cover object-center"
                priority={index === 0}
              />
            </motion.div>
          ))
        ) : (
          <motion.div
            initial={{ scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: 8, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <Image
              placeholder="blur"
              loading="eager"
              layout="fill"
              blurDataURL={`data:image/svg+xml;base64,${toBase64(
                shimmer(700, 900)
              )}`}
              src={flowerBackground}
              alt={eventName || "Wedding couple"}
              className="object-cover object-center"
              priority
            />
          </motion.div>
        )}

        <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(28,24,32,0.74)_0%,rgba(28,24,32,0.34)_45%,rgba(28,24,32,0.68)_100%)]" />

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 mx-auto flex max-w-5xl flex-col items-center"
        >
          <p className="mb-5 font-jost text-[0.58rem] font-normal uppercase tracking-[0.45em] text-white/65">
            {loading ? "Loading" : "We are getting married!"}
          </p>

          <h1 className="font-corGaramond text-[clamp(4.2rem,18vw,10.5rem)] font-light leading-[0.84] tracking-normal text-white">
            <span className="block">{firstName || eventName}</span>
            {secondName && (
              <>
                <span className="block py-1 font-corGaramond text-[clamp(2rem,8vw,4.5rem)] font-light italic leading-none text-[#228B22]">
                  &
                </span>
                <span className="block">{secondName}</span>
              </>
            )}
          </h1>

          <div className="my-5 flex w-full max-w-md items-center justify-center gap-5">
            <span className="h-px flex-1 bg-white/25" />
            <span className="h-1 w-1 rotate-45 bg-[#228B22]" />
            <span className="h-px flex-1 bg-white/25" />
          </div>

          {(eventDate || locationText) && (
            <p className="font-jost text-[0.62rem] font-normal uppercase tracking-[0.28em] text-white/60">
              {[eventDate, locationText].filter(Boolean).join(" · ")}
            </p>
          )}

          {event?.hash_tag && (
            <p className="mt-2 font-corGaramond text-lg font-black tracking-wider uppercase text-[#228B22]/80">
              {event.hash_tag}
            </p>
          )}

          {resolvedEventDate && (
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              {countdownItems.map(([label, value]) => (
                <div
                  key={label}
                  className="relative min-w-[64px] border border-white/15 bg-white/[0.04] px-4 py-3 backdrop-blur"
                >
                  <span className="absolute -left-px -top-px h-2 w-2 border-l border-t border-[#228B22]/50" />
                  <span className="absolute -bottom-px -right-px h-2 w-2 border-b border-r border-[#228B22]/50" />
                  <span className="block font-corGaramond text-3xl font-light leading-none text-white">
                    {String(value).padStart(2, "0")}
                  </span>
                  <span className="mt-1 block font-jost text-[0.45rem] font-normal uppercase tracking-[0.18em] text-white/40">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {rsvpItem && (
              <Dialog
                trigger={
                  <button className="border border-white/35 px-9 py-3 font-jost text-[0.58rem] font-normal uppercase tracking-[0.22em] text-white transition hover:border-white/75">
                    RSVP Now
                  </button>
                }
                fillScreen={rsvpItem.fillScreen}
                open={rsvpItem.open}
                onOpenChange={(open) => {
                  if (open) trackView(rsvpItem.title);
                  rsvpItem.setOpen(open);
                }}
              >
                {rsvpItem.modal}
              </Dialog>
            )}

            {asoebiItem && (
              <Dialog
                trigger={
                  <button className="border border-white/35 px-9 py-3 font-jost text-[0.58rem] font-normal uppercase tracking-[0.22em] text-white transition hover:border-white/75">
                    Aso Ebi
                  </button>
                }
                fillScreen={asoebiItem.fillScreen}
                open={asoebiItem.open}
                onOpenChange={(open) => {
                  if (open) trackView(asoebiItem.title);
                  asoebiItem.setOpen(open);
                }}
              >
                {asoebiItem.modal}
              </Dialog>
            )}
          </div>
        </motion.div>
      </section>

      {event?.welcome_msg && (
        <section className="relative border-t border-[#228B22]/15 bg-[#fff] px-6 py-20 text-center md:py-28">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(34,139,34,0.1)_0%,transparent_68%)]" />
          <div className="relative mx-auto max-w-3xl">
            {/* <p className="mb-6 font-jost text-[0.58rem] font-normal uppercase tracking-[0.45em] text-1C1820/30">
              Welcome
            </p> */}
            <p className="font-corGaramond text-2xl font-light italic leading-[1.9] text-[#401d15]/60 md:text-3xl">
              {event.welcome_msg}
            </p>
          </div>
        </section>
      )}

      <Footer slug={slugData} />
    </div>
  );
};

export default Hero;
